import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { DashboardGrid } from "./dashboard-grid";
import {
  CubeIcon,
  PhotoCameraIcon,
  CloudUploadIcon,
  SettingsIcon,
} from "./dashboard-icons";
import { ScanReview } from "./scan-review";
import { SettingsDialog } from "./settings-dialog";
import { ScanView } from "../scanner/scan-view";
import { ImageCaptureView } from "../scanner/image-capture-view";
import { ImagePreview } from "../viewer/image-preview";
import { PortraitLock } from "../portrait-lock";
import {
  createOrder,
  modelFileName,
  METRICS_MODEL_FILENAME,
} from "../../services/remote-api";
import {
  loadCompletedScans,
  saveCompletedScans,
  clearCompletedScans,
  loadAppSettings,
  saveAppSetting,
} from "../../services/scan-storage";

// key identifying one scan grid ("Shoe-left", "Torso", ...).
function scanKey(group, side) {
  return side ? `${group.bodyPart}-${side}` : group.bodyPart;
}

// key of the single image grid (shared across all body parts).
const IMAGE_KEY = "image";

// the sides to scan for one body group: a sided body part (e.g. FO) is a pair
// unless the order asked for one side only; a non-sided part has no side.
// `scan` is the order's requiredScan entry of that body group.
function scanSides(group, scan) {
  if (!group.sided) return [null];
  const side = ((scan && scan.side) || "both").toLowerCase();
  return side === "left" || side === "right" ? [side] : ["left", "right"];
}

// "left" -> "Left", for the grid labels.
function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// Multiple-scan dashboard, shown before the scan view.
export const DashboardView = ({ order, fileFormats = ["stl"] }) => {
  const theme = useTheme();
  const tint = theme.palette.primary.main;

  const {
    labId,
    webOrderId,
    webOrderUserName,
    webOrderDetails,
    extraData,
    returnUrl,
    bodyGroups,
    requiredScan,
  } = order.order;

  // the body part currently shown in the grid card, keyed by selector index.
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selected = bodyGroups[selectedIndex] ?? bodyGroups[0];

  // the scan grid currently being scanned: { group, side } or null (dashboard).
  const [activeScan, setActiveScan] = useState(null);
  // whether the image grid is currently being captured.
  const [activeImage, setActiveImage] = useState(false);
  // the saved scan/image currently being reviewed: { key, group, side } or
  // null.
  const [review, setReview] = useState(null);

  // the model file format to upload. Customers accepting several formats can
  // pick one; the last choice is restored.
  const [fileFormat, setFileFormatState] = useState(() => {
    const saved = loadAppSettings().fileFormat;
    return fileFormats.includes(saved) ? saved : (fileFormats[0] ?? "stl");
  });
  const [settingsOpen, setSettingsOpen] = useState(false);

  function setFileFormat(format) {
    saveAppSetting("fileFormat", format);
    setFileFormatState(format);
  }

  // finished scans awaiting the combined upload, keyed by scanKey().
  // Persisted per order in localStorage so a page refresh doesn't lose them.
  const [completedScans, setCompletedScans] = useState(() =>
    loadCompletedScans(labId, webOrderId),
  );
  useEffect(() => {
    saveCompletedScans(labId, webOrderId, completedScans);
  }, [labId, webOrderId, completedScans]);

  // the combined upload of all saved scans (and the optional image).
  const [uploadOpen, setUploadOpen] = useState(false); // confirm dialog
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploaded, setUploaded] = useState(false); // terminal state

  // once the data is uploaded, send the user to the order's return URL
  // (if one is set).
  useEffect(() => {
    if (uploaded && returnUrl) {
      window.location.href = returnUrl;
    }
  }, [uploaded, returnUrl]);

  // the saved 3D scans (the image is stored under IMAGE_KEY as a data URL).
  const savedScans = Object.entries(completedScans)
    .filter(([key]) => key !== IMAGE_KEY)
    .map(([, scan]) => scan);

  // upload is ready when any data is captured — 3D scans, an image, or both.
  const uploadReady =
    savedScans.length > 0 || Boolean(completedScans[IMAGE_KEY]);

  // place the order with all the saved scans' data, plus the image if one
  // was captured. With no scans (image-only order), scanTypes, dataUrls and
  // modelNames are all sent as empty arrays.
  async function uploadAll() {
    // one scan-type entry per body part, counting its scans (e.g. two sides).
    const typeCounts = new Map();
    savedScans.forEach(({ scanType }) => {
      typeCounts.set(scanType, (typeCounts.get(scanType) || 0) + 1);
    });
    const image = completedScans[IMAGE_KEY];
    // the scans that were measured. A scan whose measurement found it
    // incomplete is not: it has no results to export, and its scan is meant
    // to be taken again.
    const measuredScans = savedScans.filter(
      (scan) => scan.measurement && scan.measurement.complete,
    );
    // whether to also upload the measurement results.
    const exportMetrics = Boolean(
      order.exportMetricsData && measuredScans.length,
    );
    await createOrder({
      relatedLabId: labId,
      scanTypes: [...typeCounts].map(([type, number]) => ({
        type: `${type}_VS`,
        number,
      })),
      webOrderId,
      webOrderUserName,
      webOrderDetails,
      extraData: extraData ? JSON.stringify(extraData) : undefined,
      dataUrls: savedScans.map((scan) => scan.dataUrl),
      modelNames: savedScans.map((scan) => ({
        old: scan.modelName,
        new: modelFileName(scan.title),
        format: fileFormat,
      })),
      image: image || undefined,
      // how many of the saved scans were measured
      metricsApiUsed: measuredScans.length,
      metricsDataUrls: exportMetrics
        ? measuredScans.map((scan) => scan.measurement.dataUrl)
        : undefined,
      metricsModelNames: exportMetrics
        ? measuredScans.map((scan) => ({
            old: METRICS_MODEL_FILENAME,
            new: modelFileName(scan.title),
          }))
        : undefined,
    });
  }

  async function handleUpload() {
    setUploading(true);
    setUploadError(null);
    try {
      await uploadAll();
      // done with this order — forget its saved scans.
      clearCompletedScans(labId, webOrderId);
      setUploadOpen(false);
      setUploaded(true);
    } catch (err) {
      setUploadError(err.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  if (!selected) return null;

  // all data uploaded. If the order defined a return URL, the effect above
  // redirects there (show a brief note meanwhile); otherwise show a terminal
  // success message.
  if (uploaded) {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1400,
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          px: 4,
          textAlign: "center",
        }}
      >
        {returnUrl ? (
          <>
            <CircularProgress />
            <Typography color="text.secondary">Redirecting…</Typography>
          </>
        ) : (
          <>
            <Typography variant="h5">Data uploaded</Typography>
            <Typography color="text.secondary">
              Your data has been uploaded successfully. You can close this page
              now.
            </Typography>
          </>
        )}
      </Box>
    );
  }

  // a "done" grid was tapped — review its saved model/image instead of
  // recapturing.
  const reviewScan = review && completedScans[review.key];
  if (reviewScan && review.key === IMAGE_KEY) {
    return (
      <>
        <PortraitLock />
        <ImagePreview
          src={reviewScan}
          deleteConfirm
          onSave={() => setReview(null)}
          onDelete={() => {
            // discard the saved image and go retake it right away.
            setCompletedScans((prev) => {
              const next = { ...prev };
              delete next[review.key];
              return next;
            });
            setReview(null);
            setActiveImage(true);
          }}
        />
      </>
    );
  }
  if (reviewScan) {
    return (
      <ScanReview
        scan={reviewScan}
        group={review.group}
        order={order.order}
        onClose={(thumbnail) => {
          // the review hands back a snapshot of what it showed — after a
          // measurement that is the measured mesh, so the grid follows.
          if (thumbnail) {
            setCompletedScans((prev) => ({
              ...prev,
              [review.key]: { ...prev[review.key], thumbnail },
            }));
          }
          setReview(null);
        }}
        onMeasured={(measurement) =>
          // save the measurement right away: its mesh is what the scan is
          // reviewed as from now on, and its URL was just issued.
          setCompletedScans((prev) => ({
            ...prev,
            [review.key]: { ...prev[review.key], measurement },
          }))
        }
        onDelete={() => {
          // discard the saved scan and go rescan that grid right away.
          setCompletedScans((prev) => {
            const next = { ...prev };
            delete next[review.key];
            return next;
          });
          setReview(null);
          setActiveScan({ group: review.group, side: review.side });
        }}
        onRefreshed={(modelUrl) =>
          // remember the re-issued model URL (and its issue time). A measured
          // scan is reviewed as its measured mesh, so that is the URL that
          // was re-issued.
          setCompletedScans((prev) => {
            const saved = prev[review.key];
            const modelUrlAt = Date.now();
            const refreshed = saved.measurement
              ? { measurement: { ...saved.measurement, modelUrl, modelUrlAt } }
              : { modelUrl, modelUrlAt };
            return { ...prev, [review.key]: { ...saved, ...refreshed } };
          })
        }
      />
    );
  }

  // a scan grid was tapped — take over the screen with the scan view for
  // that body part (and side). Saving returns the scan info back here.
  if (activeScan) {
    const { group, side } = activeScan;
    const scanOrder = {
      ...order,
      order: {
        ...order.order,
        bodyGroup: group,
        requiredScan: { type: group.bodyPart, side },
      },
    };
    return (
      <ScanView
        order={scanOrder}
        fileFormat={fileFormat}
        onSaved={(scanInfo) => {
          // the scan carries the issue time of its own model URL, and of
          // its measured mesh in the measurement.
          setCompletedScans((prev) => ({
            ...prev,
            [scanKey(group, side)]: scanInfo,
          }));
          setActiveScan(null);
        }}
        onBack={() => setActiveScan(null)}
      />
    );
  }

  // the image grid was tapped — take over the screen with the photo capture
  // view. Saving returns the image data URL back here.
  if (activeImage) {
    return (
      <ImageCaptureView
        onSaved={(image) => {
          setCompletedScans((prev) => ({ ...prev, [IMAGE_KEY]: image }));
          setActiveImage(false);
        }}
        onBack={() => setActiveImage(false)}
      />
    );
  }

  const name = selected.bodyPartName || selected.bodyPart;
  // the side(s) this order asks for on the selected body part — bodyGroups is
  // built from requiredScan, so the two arrays line up by index.
  const sides = scanSides(
    selected,
    requiredScan && requiredScan[selectedIndex],
  );

  // done state, thumbnail, and tap action of one scan grid: scan when empty,
  // review the saved model when done (a done grid shows the scan's thumbnail).
  const scanGridProps = (group, side) => {
    const key = scanKey(group, side);
    const scan = completedScans[key];
    return {
      done: Boolean(scan),
      thumbnail: scan && scan.thumbnail,
      onClick: () =>
        scan ? setReview({ key, group, side }) : setActiveScan({ group, side }),
    };
  };

  // same for the single image grid (shared across body parts): capture a
  // photo when empty, preview it when done (a done grid shows the image).
  const imageGridProps = () => {
    const image = completedScans[IMAGE_KEY];
    return {
      done: Boolean(image),
      thumbnail: image,
      padded: true, // add some paddings at the top and bottom
      onClick: () =>
        image ? setReview({ key: IMAGE_KEY }) : setActiveImage(true),
    };
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        display: "flex",
        flexDirection: "column",
        backgroundColor: alpha(tint, 0.2),
      }}
    >
      {/* HEAD: title bar */}
      <Box
        sx={{
          pt: "max(12px, env(safe-area-inset-top))",
          pb: 1,
          px: 2.5,
          textAlign: "center",
          position: "relative",
        }}
      >
        <Typography fontSize={18} fontWeight="bold">
          Scan Dashboard
        </Typography>
        {webOrderId && (
          <Typography variant="body2" color="text.secondary" mt={0.25}>
            Order {webOrderId}
          </Typography>
        )}
        {/* settings — only when there is something to choose (e.g. several
            accepted file formats) */}
        {fileFormats.length > 1 && (
          <Box
            component="button"
            type="button"
            aria-label="Settings"
            onClick={() => setSettingsOpen(true)}
            sx={{
              position: "absolute",
              right: 12,
              top: "max(12px, env(safe-area-inset-top))",
              border: "none",
              background: "none",
              p: 0.5,
              color: tint,
              cursor: "pointer",
              display: "flex",
            }}
          >
            <SettingsIcon />
          </Box>
        )}
      </Box>

      {/* body-part selector */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          overflowX: "auto",
          px: 1,
          py: 1,
          backgroundColor: "#fff",
          boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
          zIndex: 1,
          flexShrink: 0,
        }}
      >
        {bodyGroups.map((group, index) => {
          const chipSelected = index === selectedIndex;
          const chipName = group.bodyPartName || group.bodyPart;
          return (
            <Box
              key={group.bodyPart}
              component="button"
              type="button"
              onClick={() => setSelectedIndex(index)}
              sx={{
                border: "none",
                borderRadius: "8px",
                px: 1.5,
                py: 1,
                fontSize: 16,
                fontWeight: "bold",
                whiteSpace: "nowrap",
                cursor: "pointer",
                userSelect: "none",
                WebkitTapHighlightColor: "transparent",
                color: chipSelected ? "#fff" : tint,
                backgroundColor: chipSelected ? tint : "transparent",
                transition: "background-color 0.2s, color 0.2s",
              }}
            >
              {chipName}
            </Box>
          );
        })}
      </Box>

      {/* BODY: grids of the selected body part. Scrolls if the screen is too
          short. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          px: 2,
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            my: "auto",
            py: 1.5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* scan grid(s) — two when sided */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
            }}
          >
            {sides.length > 1 ? (
              sides.map((side) => (
                <DashboardGrid
                  key={side}
                  framed
                  label={`${capitalize(side)} ${name}`}
                  {...scanGridProps(selected, side)}
                >
                  <CubeIcon />
                </DashboardGrid>
              ))
            ) : (
              // one grid only: a non-sided body part, or a sided one the
              // order wants scanned on a single side
              <DashboardGrid
                framed
                wide
                label={sides[0] ? `${capitalize(sides[0])} ${name}` : name}
                {...scanGridProps(selected, sides[0])}
              >
                <CubeIcon />
              </DashboardGrid>
            )}
          </Box>

          {/* image capture grid */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <DashboardGrid wide label="Image" {...imageGridProps()}>
              <PhotoCameraIcon />
            </DashboardGrid>
          </Box>
        </Box>
      </Box>

      {/* FOOTER: upload button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: 2,
          pt: 0.75,
          pb: 0.5,
          backgroundColor: "#fff",
          boxShadow: "0 -2px 6px rgba(0,0,0,0.25)",
          zIndex: 1,
          flexShrink: 0,
        }}
      >
        <Box
          component="button"
          type="button"
          aria-label="Upload scans"
          disabled={!uploadReady}
          onClick={() => {
            setUploadError(null);
            setUploadOpen(true);
          }}
          sx={{
            border: "none",
            background: "none",
            p: 0.5,
            color: uploadReady ? tint : "grey.500",
            opacity: uploadReady ? 1 : 0.4,
            cursor: uploadReady ? "pointer" : "default",
            display: "flex",
          }}
        >
          <CloudUploadIcon />
        </Box>
      </Box>

      {/* footer logo — sized to the banner, extra bottom padding for the
          safe area */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          pt: 0.5,
          pb: "max(10px, env(safe-area-inset-bottom))",
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/banner.png`}
          alt="SnugFit Solutions"
          sx={{ height: 64, maxWidth: "80vw", objectFit: "contain" }}
        />
      </Box>

      {/* settings */}
      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        fileFormats={fileFormats}
        fileFormat={fileFormat}
        onFileFormatChange={setFileFormat}
      />

      {/* combined-upload confirmation */}
      <Dialog
        open={uploadOpen}
        onClose={() => !uploading && setUploadOpen(false)}
        sx={{ zIndex: 1500 }}
      >
        <DialogTitle>Upload all the scan data?</DialogTitle>
        {uploadError && (
          <DialogContent>
            <Alert severity="error">{uploadError}</Alert>
          </DialogContent>
        )}
        <DialogActions>
          <Button
            onClick={handleUpload}
            disabled={uploading}
            startIcon={
              uploading ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {uploading ? "Uploading…" : uploadError ? "Retry" : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

DashboardView.propTypes = {
  order: PropTypes.object.isRequired,
  fileFormats: PropTypes.arrayOf(PropTypes.string),
};
